/**
 * The compact ticket card the assistant drops into a reply.
 *
 * Whenever an answer references or creates a ticket, the user should be one
 * click from the real thing rather than left holding a number to copy. The
 * route comes from `TicketRef.url_path`, which the contract defines as an
 * in-app path — never an absolute URL — so it goes straight into a router
 * `<Link>`; `/tickets/{id}` is the fallback if a provider ever sends nothing.
 */

import { ChevronRight, Icon, Ticket as TicketIcon } from "@norican/icons";
import { Badge, type BadgeTone, Button, Card, cn, Typography } from "@norican/react";
import { Link } from "react-router";

import { useT } from "@/i18n";
import { useKiosk } from "@/lib/api";

import type { TicketRef, TicketStatusCode } from "./contract";
import { TranslatedText } from "./localized";

/**
 * Status colour, keyed on the portal's stable codes rather than on Ivanti's
 * tenant-specific vocabulary. `UNKNOWN` is a real, expected value — an Ivanti
 * administrator can add a status this build has never seen — so it renders
 * neutrally instead of being hidden.
 */
const STATUS_TONES: Record<TicketStatusCode, BadgeTone> = {
  LOGGED: "info",
  ACTIVE: "accent",
  WAITING_CUSTOMER: "warning",
  WAITING_THIRD_PARTY: "warning",
  WAITING_RESOLUTION: "warning",
  PENDING_APPROVAL: "warning",
  APPROVED: "success",
  APPROVAL_REJECTED: "danger",
  IN_PROCUREMENT: "info",
  RESOLVED: "success",
  FULFILLED: "success",
  CLOSED: "neutral",
  CANCELLED: "neutral",
  UNKNOWN: "neutral",
};

const KNOWN_STATUSES = Object.keys(STATUS_TONES) as TicketStatusCode[];

function asStatusCode(value: string): TicketStatusCode {
  return (KNOWN_STATUSES as readonly string[]).includes(value)
    ? (value as TicketStatusCode)
    : "UNKNOWN";
}

export interface TicketRefCardProps {
  ticket: TicketRef;
  className?: string;
}

export function TicketRefCard({ ticket, className }: TicketRefCardProps) {
  const { t } = useT("tickets");
  const status = asStatusCode(ticket.status_code);
  const to = ticket.url_path.startsWith("/")
    ? ticket.url_path
    : `/tickets/${encodeURIComponent(ticket.ticket_id)}`;

  return (
    <Card
      variant="outline"
      padding="none"
      interactive
      className={cn("hover:border-accent-border transition-colors", className)}
    >
      <Link
        to={to}
        className="flex items-center gap-3 px-4 py-3 no-underline focus-visible:outline-none"
      >
        <Icon icon={TicketIcon} size="sm" tone="accent" />

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="label" className="font-mono">
              {ticket.ticket_number}
            </Typography>
            <Badge tone="neutral" variant="outline" size="xs">
              {t(`kind.${ticket.kind}`)}
            </Badge>
            <Badge tone={STATUS_TONES[status]} variant="subtle" size="xs">
              {t(`status.${status}`)}
            </Badge>
          </div>

          <TranslatedText
            value={ticket.subject}
            hideAffordance
            render={(text) => (
              <Typography variant="body-sm" tone="muted" className="line-clamp-1">
                {text}
              </Typography>
            )}
          />
        </div>

        <Icon icon={ChevronRight} size="sm" tone="accent" />
      </Link>
    </Card>
  );
}

export interface TicketRefListProps {
  tickets: TicketRef[];
  className?: string;
}

/**
 * The "Related tickets" group under an answer. Renders nothing when empty.
 *
 * A turn streams at most a handful of cards (the backend caps a
 * `list_my_tickets` listing at five), so the group always ends with the way to
 * the full list.
 */
export function TicketRefList({ tickets, className }: TicketRefListProps) {
  const { t } = useT("assistant");
  // No ticket list on a shared device (1 Oct).
  const kiosk = useKiosk();

  if (tickets.length === 0) {
    return null;
  }

  return (
    <section className={cn("flex flex-col gap-2", className)} aria-label={t("ticketRefs.title")}>
      <Typography as="h4" variant="label">
        {t("ticketRefs.title")}
      </Typography>
      <ul className="flex list-none flex-col gap-2 p-0">
        {tickets.map((ticket) => (
          <li key={ticket.ticket_id}>
            <TicketRefCard ticket={ticket} />
          </li>
        ))}
      </ul>
      {kiosk === null ? (
        <Button
          render={<Link to="/tickets" />}
          variant="ghost"
          tone="accent"
          size="sm"
          endIcon={<Icon icon={ChevronRight} size="sm" />}
          className="self-start"
        >
          {t("ticketRefs.seeAll")}
        </Button>
      ) : null}
    </section>
  );
}
